/**
 * Number formatting.
 *
 * IC50 values are never formatted here -- the backend sends `ic50_display`
 * already written the way it should be read, bounds included. Re-deriving it in
 * the browser is how a figure and a table end up disagreeing.
 */

export function sig(value, digits = 4) {
  if (value === null || value === undefined) return "—";
  if (value === 0) return "0";
  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const rounded = Number(value.toPrecision(digits));
  if (Math.abs(rounded) >= 1e-4 && Math.abs(rounded) < 1e6) {
    return rounded.toFixed(Math.max(0, digits - 1 - exponent));
  }
  return rounded.toExponential(digits - 1);
}

/**
 * `ic50_display` with the unit attached, as the exported figures write it.
 *
 * A verdict such as "no measurable inhibition" is not a quantity, so it takes no
 * unit; "> 500" is already a comparison, so it takes no "=" either.
 */
export function ic50Annotation(label, display) {
  const quantity = /^[\d<>]/.test(display);
  const value = quantity ? `${display} µM` : display;
  return value.startsWith("<") || value.startsWith(">")
    ? `${label} ${value}`
    : `${label} = ${value}`;
}

export function percent(value, digits = 1) {
  return value === null || value === undefined ? "—" : `${value.toFixed(digits)} %`;
}

export function fraction(value, digits = 1) {
  return value === null || value === undefined ? "—" : `${(100 * value).toFixed(digits)} %`;
}

/**
 * Dates read as `08-Oct-2025`, never as `08/10/2025`.
 *
 * A locale-formatted date is ambiguous across the readers of this data -- 08/10
 * is the eighth of October to one and the tenth of August to another -- and an
 * experiment date that can be misread is worse than one that is a little longer
 * to write. The month is spelled, so it cannot be.
 */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function day(value) {
  if (!value) return "—";
  // A bare `YYYY-MM-DD` is parsed as UTC midnight, which is the previous day in
  // any timezone west of Greenwich. An experiment date has no timezone; read it
  // as written rather than through the browser's.
  const bare = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (bare) return `${bare[3]}-${MONTHS[Number(bare[2]) - 1]}-${bare[1]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
}

export function dateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${day(value)} ${time}`;
}

/** Signed relative change, for the "what did marking that point do" readout. */
export function delta(before, after) {
  if (before === null || after === null || before === undefined || after === undefined) return null;
  if (!before) return null;
  const change = (100 * (after - before)) / before;
  // Marking a point moves one condition's fit, not the other two. Reporting
  // "+0.0 %" against the untouched ones only buries the one that did change.
  if (Math.abs(change) < 0.05) return null;
  return `${change >= 0 ? "+" : ""}${change.toFixed(1)} %`;
}
